import "./provide-feedback-modal.scss";

import { Alert, AlertTitle, Button, Dialog, DialogContent, DialogTitle, Link, TextField } from "@mui/material";
import Typography from "@mui/material/Typography";
import { MailIcon, PhoneIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generateUID } from "verdi-common/utils/HelperFunctions";
import { SessionHandler } from "verdi-web-client/APICommands/SessionHandler";

import SimpleBarCompatibility from "@/components/simple-bar-compatibility/simple-bar-compatibility";
import { SupportEmailLink } from "@/components/support-email-link";
import { NAMESPACES } from "@/i18n/constants";

interface ProvideFeedbackModalProps {
    open: boolean;
    handleClose: () => void;
}

export function ProvideFeedbackModal({ open, handleClose }: ProvideFeedbackModalProps) {
    const uid = generateUID("ProvideFeedbackModal");
    const { t } = useTranslation(NAMESPACES.FEATURES.NAVIGATION, {
        keyPrefix: "provide-feedback-modal.modal",
    });

    const [name, setName] = useState("");
    const [feedbackString, setFeedbackString] = useState("");
    const [sendStatus, setSendStatus] = useState("composing");
    const [validationError, setValidationError] = useState({ name: false, feedback: false });

    const resetForm = () => {
        setSendStatus("composing");
        setFeedbackString("");
        setName("");
        setValidationError({ name: false, feedback: false });
    };

    const validateForm = () => {
        // Both strings must be non-empty
        if (!name.trim() || !feedbackString.trim()) {
            setSendStatus("error");
            setValidationError({ name: !name.trim(), feedback: !feedbackString.trim() });
            return false;
        }
        return true;
    };

    const handleSubmit = () => {
        setSendStatus("sending");

        // Reject submit if not valid
        if (!validateForm()) {
            return;
        }
        /**
         * API currently only accepts a single string - this feedback form is not used much,
         * so for convenience of avoiding changes to API, we append name directly to the feedback string
         */
        const feedbackWithName = `${name} - ${feedbackString}`;

        SessionHandler.doFetch("users/feedback", {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            method: "POST",
            redirect: "follow",
            body: JSON.stringify({ feedback: feedbackWithName }),
        })
            .then(() => {
                setSendStatus("success");
            })
            .catch(() => {
                setSendStatus("error");
            });
    };

    const onClose = () => {
        resetForm();
        handleClose();
    };

    const className = `ProvideFeedbackModal ProvideFeedbackModal--${sendStatus}`;
    return (
        <Dialog onClose={onClose} open={open} className={className} id={uid} maxWidth={"md"} fullWidth>
            <DialogTitle>{t("title")}</DialogTitle>
            <DialogContent>
                <div className={`SuccessOverlay`}>
                    <Typography variant={"h3"} className={"ThanksText"}>
                        {t("success-title")}
                    </Typography>
                    <Typography variant={"subtitle1"}>{t("success-body")}</Typography>
                    <Button
                        className={"FinishedFeedbackButton"}
                        variant={"contained"}
                        onClick={() => {
                            resetForm();
                            handleClose();
                        }}
                    >
                        {t("success-close")}
                    </Button>
                </div>
                <SimpleBarCompatibility>
                    <Alert severity={"warning"} className={"SupportAlert"}>
                        <AlertTitle>{t("support-alert-title")}</AlertTitle>
                        <Typography variant={"body2"} gutterBottom>
                            {t("support-alert-body")}
                        </Typography>
                        <Typography variant={"body2"}>
                            <MailIcon aria-hidden className={"inline-block align-middle mr-1 size-4"} />
                            <SupportEmailLink />
                            {" — "}
                            <strong>{t("support-email-preferred")}</strong>
                            {` ${t("support-email-note")}`}
                        </Typography>
                        <Typography variant={"body2"}>
                            <PhoneIcon aria-hidden className={"inline-block align-middle mr-1 size-4"} />
                            <Link href={"tel:+18555998542"} underline={"always"}>
                                {t("support-phone-label")}
                            </Link>
                            {` — ${t("support-phone-note")}`}
                        </Typography>
                    </Alert>
                    <Typography variant={"subtitle1"} className={"FeedbackDescription"}>
                        {t("description")}
                    </Typography>
                    <TextField
                        className={"FeedbackTextInput"}
                        error={validationError.name}
                        label={t("name-label")}
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                        }}
                        helperText={validationError.name ? t("name-required") : ""}
                    />

                    <TextField
                        className={"FeedbackTextInput"}
                        error={validationError.feedback}
                        label={t("feedback-label")}
                        multiline
                        minRows={6}
                        value={feedbackString}
                        onChange={(e) => {
                            setFeedbackString(e.target.value);
                        }}
                        helperText={validationError.feedback ? t("feedback-required") : ""}
                    />
                </SimpleBarCompatibility>
                <Button className={"SubmitButton"} variant={"contained"} onClick={handleSubmit}>
                    {sendStatus === "error" ? t("submit-failed") : t("submit")}
                </Button>
            </DialogContent>
        </Dialog>
    );
}
